import pino, { Logger as PinoLogger, LoggerOptions } from 'pino';
import { Config, LogLevel } from '@doc-abode/data-models';

// Copied from pino types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PinoFnArgs = (obj: unknown, msg?: string, ...args: any[]) => void;

type LogLevelMap = Record<LogLevel, number>;

type LogMeta = {
    traceId?: string;
    orgConfig?: Config;
    [key: string]: unknown;
};

interface Logger {
    /**
     * Log at 'trace' level the given message and object.
     *
     * @param message: the log message to write
     * @param meta: additional meta data to include in the log payload. If this includes an
     * orgConfig property which has a logLevel property, the lowest of the LOG_LEVEL environment
     * variable and logLevel will be used to determine whether the log should be written.
     */
    trace: (message: string, meta?: LogMeta) => void;
    /**
     * Log at 'trace' level the given message and object.
     *
     * @param message: the log message to write
     * @param meta: additional meta data to include in the log payload. If this includes an
     * orgConfig property which has a logLevel property, the lowest of the LOG_LEVEL environment
     * variable and logLevel will be used to determine whether the log should be written.
     */
    debug: (message: string, meta?: LogMeta) => void;
    /**
     * Log at 'trace' level the given message and object.
     *
     * @param message: the log message to write
     * @param meta: additional meta data to include in the log payload. If this includes an
     * orgConfig property which has a logLevel property, the lowest of the LOG_LEVEL environment
     * variable and logLevel will be used to determine whether the log should be written.
     */
    info: (message: string, meta?: LogMeta) => void;
    /**
     * Log at 'trace' level the given message and object.
     *
     * @param message: the log message to write
     * @param meta: additional meta data to include in the log payload. If this includes an
     * orgConfig property which has a logLevel property, the lowest of the LOG_LEVEL environment
     * variable and logLevel will be used to determine whether the log should be written.
     */
    warn: (message: string, meta?: LogMeta) => void;
    /**
     * Log at 'trace' level the given message and object.
     *
     * @param message: the log message to write
     * @param meta: additional meta data to include in the log payload. If this includes an
     * orgConfig property which has a logLevel property, the lowest of the LOG_LEVEL environment
     * variable and logLevel will be used to determine whether the log should be written.
     */
    error: (message: string, meta?: LogMeta) => void;
    /**
     * Log at 'trace' level the given message and object.
     *
     * @param message: the log message to write
     * @param meta: additional meta data to include in the log payload. If this includes an
     * orgConfig property which has a logLevel property, the lowest of the LOG_LEVEL environment
     * variable and logLevel will be used to determine whether the log should be written.
     */
    fatal: (message: string, meta?: LogMeta) => void;
}

const { NODE_ENV, _X_AMZN_TRACE_ID, LOG_LEVEL = LogLevel.ERROR } = process.env;

// Define the log levels, which match those used by pino.
const logLevels: LogLevelMap = {
    [LogLevel.TRACE]: 10,
    [LogLevel.DEBUG]: 20,
    [LogLevel.INFO]: 30,
    [LogLevel.WARN]: 40,
    [LogLevel.ERROR]: 50,
    [LogLevel.FATAL]: 60,
};

// Define base logger options.
export const loggerOptions: LoggerOptions = {
    name: 'DocAbodeLogger',
    level: LogLevel.TRACE,
    base: {
        // traceId: Trace ID to help with debugging. Defaults to the value of the _X_AMZN_TRACE_ID
        // environment variable, which is set automatically for Lambdas that use the X-Ray SDK.
        traceId: _X_AMZN_TRACE_ID,
    },
    formatters: {
        // This formatter sets the log level to a friendly name, e.g. 'info', rather than a number.
        level: (label) => {
            return {
                level: label,
            };
        },
    },
};

// In development mode, format the logs for a friendly terminal output.
if (NODE_ENV === 'development') {
    loggerOptions.transport = {
        target: 'pino-pretty',
        options: {
            colorize: true,
        },
    };
}

const checkLogLevel = (
    logFn: PinoFnArgs,
    logLevel: number,
    message: string,
    meta?: LogMeta,
): void => {
    // Get the base log level, which is the minimum log level that should be logged, from the
    // LOG_LEVEL environment variable.
    let baseLogLevel = logLevels[LOG_LEVEL as LogLevel];

    // If an organisation config is provided, check if the log level for the organisation is lower
    // than the base log level. If so, use the organisation log level instead.
    if (meta?.orgConfig?.logLevel) {
        const orgLogLevel = logLevels[meta.orgConfig.logLevel];
        const minLogLevel = Math.min(baseLogLevel, orgLogLevel);

        // Math.min can return NaN, so ensure the value is an int before assigning to baseLogLevel.
        if (Number.isInteger(minLogLevel)) {
            baseLogLevel = minLogLevel;
        }
    }

    // If the log level of the message is greater than or equal to the base log level, forward the
    // payload to the logger, after some changes to add a friendly level name and the organisation.
    if (logLevel >= baseLogLevel) {
        const adaptedMeta: LogMeta = { ...meta };

        if (NODE_ENV !== 'development') {
            adaptedMeta.levelNumber = logLevel;
        }

        if (adaptedMeta.orgConfig) {
            adaptedMeta.organisation = adaptedMeta.orgConfig.org;
            delete adaptedMeta.orgConfig;
        }

        logFn(adaptedMeta, message);
    }
};

export const createLogger = (logger: PinoLogger): Logger => ({
    trace: (message: string, meta?: LogMeta) =>
        checkLogLevel((...args) => logger.trace(...args), logLevels.trace, message, meta),
    debug: (message: string, meta?: LogMeta) =>
        checkLogLevel((...args) => logger.debug(...args), logLevels.debug, message, meta),
    info: (message: string, meta?: LogMeta) =>
        checkLogLevel((...args) => logger.info(...args), logLevels.info, message, meta),
    warn: (message: string, meta?: LogMeta) =>
        checkLogLevel((...args) => logger.warn(...args), logLevels.warn, message, meta),
    error: (message: string, meta?: LogMeta) =>
        checkLogLevel((...args) => logger.error(...args), logLevels.error, message, meta),
    fatal: (message: string, meta?: LogMeta) =>
        checkLogLevel((...args) => logger.fatal(...args), logLevels.fatal, message, meta),
});

export const logger = createLogger(pino(loggerOptions));
